import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Syringe, DollarSign, TrendingUp, Calendar, 
  Plus, RefreshCw, CreditCard, Banknote, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard`, {
        withCredentials: true
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPaymentIcon = (tipo) => {
    switch (tipo?.toUpperCase()) {
      case 'DÉBITO':
      case 'DEBITO':
      case 'CRÉDITO':
      case 'CREDITO':
        return <CreditCard className="w-4 h-4" />;
      case 'EFECTIVO':
        return <Banknote className="w-4 h-4" />;
      default:
        return <DollarSign className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner w-10 h-10"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Dashboard</h1>
            <p className="text-slate-500 mt-1">Resumen de operaciones del día</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={fetchDashboard}
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
            <Link to="/vaccinations/new">
              <Button className="btn-accent gap-2" data-testid="new-vaccination-btn">
                <Plus className="w-4 h-4" />
                Nueva Vacunación
              </Button>
            </Link>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Today's Revenue */}
          <Card className="metric-card" data-testid="today-revenue-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Ingresos Hoy
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                  {formatCurrency(dashboard?.today?.total_monto)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </Card>

          {/* Today's Vaccinations */}
          <Card className="metric-card border-l-emerald-500" data-testid="today-vaccinations-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Vacunaciones Hoy
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                  {dashboard?.today?.total_vacunas || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Syringe className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </Card>

          {/* Monthly Revenue */}
          <Card className="metric-card border-l-amber-500" data-testid="month-revenue-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Ingresos del Mes
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                  {formatCurrency(dashboard?.month?.total_monto)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </Card>

          {/* Monthly Vaccinations */}
          <Card className="metric-card border-l-purple-500" data-testid="month-vaccinations-card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Vacunaciones del Mes
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">
                  {dashboard?.month?.total_vacunas || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payments Breakdown */}
          <Card className="lg:col-span-1" data-testid="payments-breakdown-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Desglose por Tipo de Pago
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.payments?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.payments.map((payment, idx) => (
                    <div 
                      key={`${payment.tipo}-${idx}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          {getPaymentIcon(payment.tipo)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700">{payment.tipo}</p>
                          <p className="text-xs text-slate-500">{payment.count} operaciones</p>
                        </div>
                      </div>
                      <p className="text-sm font-semibold text-slate-900 font-mono">
                        {formatCurrency(payment.total)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay pagos registrados hoy</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Vaccines */}
          <Card className="lg:col-span-1" data-testid="top-vaccines-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Vacunas del Día
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.vaccines?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.vaccines.map((vaccine, idx) => (
                    <div 
                      key={`${vaccine.vacuna}-${idx}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm">
                          {idx + 1}
                        </div>
                        <p className="text-sm font-medium text-slate-700">{vaccine.vacuna}</p>
                      </div>
                      <span className="badge-neutral font-mono">
                        {vaccine.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Syringe className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay vacunaciones registradas hoy</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Vaccinations */}
          <Card className="lg:col-span-1" data-testid="recent-vaccinations-card">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Últimas Vacunaciones
              </CardTitle>
              <Link to="/vaccinations">
                <Button variant="ghost" size="sm" className="gap-1 text-blue-600 hover:text-blue-700">
                  Ver todas
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {dashboard?.recent?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent.map((vacc, idx) => (
                    <div 
                      key={vacc.id || `${vacc.nombre_completo}-${idx}`}
                      className="p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-700">
                            {vacc.nombre_completo}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {vacc.vacuna} - {vacc.dosis}
                          </p>
                        </div>
                        <span className="text-xs text-slate-400 font-mono">
                          {vacc.fecha_vacunacion}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No hay vacunaciones recientes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
